import { Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom'
import AppShell from '@/shell/AppShell'
import NotFound from '@/routes/NotFound'
import Systeme from '@/routes/Systeme'
import Emails from '@/routes/Emails'
import Journey from '@/routes/journey/Journey'
import Till from '@/routes/till/Till'
import Accueil from '@/routes/Accueil'
import Campagnes from '@/routes/campagnes/Campagnes'
import Stepper from '@/routes/campagnes/Stepper'
import Activation from '@/routes/campagnes/Activation'
import CampagneDetail from '@/routes/campagnes/Detail'
import Cadeaux from '@/routes/cadeaux/Cadeaux'
import Demandes from '@/routes/demandes/Demandes'
import Supports from '@/routes/supports/Supports'
import Kiosque from '@/routes/supports/Kiosque'
import Affichage from '@/routes/supports/Affichage'
import Clients from '@/routes/clients/Clients'
import Resultats from '@/routes/resultats/Resultats'
import Etablissements from '@/routes/onboarding/Etablissements'
import EnterEtablissement, { MonEtablissement } from '@/routes/onboarding/Etablissement'
import Reponses from '@/routes/reponses/Reponses'
import Guide from '@/routes/onboarding/Guide'
import Identite from '@/routes/identite/Identite'
import { routesA } from '@/routes/routes-a'
import { routesB } from '@/routes/routes-b'
import Connexion from '@/routes/connexion/Connexion'
import CreerMonEspace from '@/routes/connexion/CreerMonEspace'
import { ServerMirror } from '@/lib/server'
import { ServerWrites } from '@/lib/writes'

/** A redirect that keeps the query string and forces a few params — the retired doors of D-63 / D-64. */
function Redirect({ to, force }: { to: string; force?: Record<string, string> }) {
  const [params] = useSearchParams()
  const p = new URLSearchParams(params); Object.entries(force ?? {}).forEach(([k, v]) => p.set(k, v))
  return <Navigate to={`${to}?${p.toString()}`} replace />
}

/** lib-pack-17 · the Demandes library has its own page: Cadeaux.tsx's stop-gap « Demandes » tab (?onglet=demandes) is moved
 *  there, never duplicated — its old door lands on /demandes with the rest of the query string. */
function CadeauxOrDemandes() {
  const [params] = useSearchParams()
  if (params.get('onglet') === 'demandes') { const p = new URLSearchParams(params); p.delete('onglet'); const q = p.toString(); return <Navigate to={`/demandes${q ? `?${q}` : ''}`} replace /> }
  return <Cadeaux />
}

/** Batch 20 · the Editeur is retired: /campagnes/:id/parcours opens the builder on that campaign (its next version). */
function ToBuilder() {
  const { id } = useParams()
  return <Navigate to={`/campagnes/nouvelle?brouillon=${id}&etape=1`} replace />
}

export default function App() {
  return (
    <>
    {/* 2b-2: with an API, an editor's refused write that no page claimed is said here (lib/writes) */}
    <ServerWrites />
    <Routes>
      {/* the ids row (2b-1): with an API, every route that reads the campaigns, the roues, the gifts or the establishments
          renders once the server's are read (lib/server); the demo renders them at once */}
      <Route element={<ServerMirror />}>
      {/* customer surfaces — no merchant chrome */}
      <Route path="/d/:code" element={<Journey />} />
      <Route path="/kiosque/:code" element={<Kiosque />} />
      {/* v3.3 (WF-049) · a support's passive display: the QR on a screen, no interaction, no merchant chrome */}
      <Route path="/affichage/:id" element={<Affichage />} />
      </Route>
      {/* staff verifier — dedicated operational surface (WF-03) */}
      <Route path="/till" element={<Till />} />
      {/* /mes-cadeaux (WF-02, routes/gifts) is retired (I-2 vet § Mine): the customer's wallet is the player's page, the merchant
          app draws no gift QR (C.4 — a gift has its code) */}
      {/* onboarding & sign-in — outside the merchant chrome (WF-04) */}
      {/* WF-047 (the connexion ruling 19/10) · the two doors sit outside the mirror and the shell: no session is needed to reach
          them; lib/auth exchanges the #token and a lost session at the root comes here with ?suite= */}
      <Route path="/connexion" element={<Connexion />} />
      <Route path="/creer-mon-espace" element={<CreerMonEspace />} />
      <Route path="/demarrer" element={<Navigate to="/guide" replace />} /> {/* MA-8 vet · the old ON-01…ON-09 flow is retired: one setup, the guide */}
      <Route element={<ServerMirror />}>
      <Route element={<AppShell />}>
        <Route path="/" element={<Accueil />} />
        <Route path="/campagnes" element={<Campagnes />} />
        <Route path="/campagnes/express" element={<Redirect to="/campagnes/nouvelle" force={{ reset: '1' }} />} /> {/* D-64 (11/09) · the express is retired — the stepper, prefilled, is the one door */}
        <Route path="/campagnes/nouvelle" element={<Stepper />} /> {/* D-52 … D-62 · the stepper in the owner's order */}
        <Route path="/campagnes/:id/parcours" element={<ToBuilder />} />
        <Route path="/campagnes/:id/activation" element={<Activation />} />
        <Route path="/campagnes/:id" element={<CampagneDetail />} />
        <Route path="/cadeaux" element={<CadeauxOrDemandes />} />
        <Route path="/supports" element={<Supports />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/resultats" element={<Resultats />} />
        <Route path="/etablissements" element={<Etablissements />} />
        {/* D-31 (05/09): opening an establishment enters its context and lands on Accueil */}
        <Route path="/etablissement" element={<MonEtablissement />} />
        <Route path="/etablissement/:id" element={<EnterEtablissement />} />
        <Route path="/reponses" element={<Reponses />} />
        <Route path="/guide" element={<Guide />} />
        {/* the handover menu (lib/nav): Bibliothèques › Roues · Cadeaux are Cadeaux.tsx's pages, /roues its door (the ?onglet= the
            page reads); Demandes is its own page since lib-pack-17 (routes/demandes); « Cadeaux gagnés » is Clients' page
            /clients?vue=cadeaux, /cadeaux/gagnes its door */}
        <Route path="/roues" element={<Redirect to="/cadeaux" force={{ onglet: 'roues' }} />} />
        <Route path="/demandes" element={<Demandes />} />
        <Route path="/cadeaux/gagnes" element={<Redirect to="/clients" force={{ vue: 'cadeaux' }} />} />
        <Route path="/identite" element={<Identite />} /> {/* D-70 · « Marque », under Paramètres */}
        {/* /equipe is Builder B's one-list screen (routes-b, batch 22); the EquipeAccueil
            fallback of batch 21 is deleted with batch 22 on main. */}
        {/* Batch 21 · Intégrations lives under Paramètres (Scope v5 §4 · Builder B batch 22) */}
        <Route path="/integrations" element={<Navigate to="/parametres/integrations" replace />} />
        {/* the retired doors keep their query string: D-70's « Suivre » opens on Résultats; Version 21's /parametres/marque is Marque */}
        <Route path="/suivre" element={<Redirect to="/resultats" />} />
        <Route path="/etablissements/ajouter" element={<Redirect to="/etablissements" force={{ ajouter: '1' }} />} />
        <Route path="/marque" element={<Redirect to="/identite" />} />
        <Route path="/parametres/marque" element={<Redirect to="/identite" />} />
        {/* D-78 (02/10) · WF-06 retired as screens: RJ-01…06 and RJ-08…12 redirect to the campaign
            builder — a campaign's visits, exposure, automation and versions are the builder's rows now.
            RJ-07 « Rythme » stays (the window's reader, routes-a). These identical paths sit before
            {routesA}, so they win the ranking tie and Builder A's registrations go quiet without
            the Orchestrator touching routes-a.tsx. */}
        <Route path="/campagnes/parcours" element={<Redirect to="/campagnes/nouvelle" />} />
        {['etape', 'compte-a-rebours', 'exposition', 'apres-etape-5', 'automatisations', 'migration', 'retention', 'variante', 'quiz', 'quiz/question'].map(p => (
          <Route key={`d78-${p}`} path={`/campagnes/parcours/${p}`} element={<Redirect to="/campagnes/nouvelle" />} />
        ))}
        {routesA}
        {routesB}
        <Route path="/_systeme" element={<Systeme />} />
        <Route path="/_emails" element={<Emails />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      </Route>
    </Routes>
    </>
  )
}
